
import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Customer, Subscription, Invoice, AIUsageLog, CreditVelocity, CustomerWorkspace } from '../types';
import { api } from '../services/api';
import SubscriptionTable from './SubscriptionTable';
import InvoiceTable from './InvoiceTable';
import { CURRENCIES, formatValue } from '../constants';
import { useCurrency } from './CurrencyContext';
import { Layers, ShieldCheck, Plus, Sparkles, ExternalLink } from 'lucide-react';

interface CustomerDetailProps {
  customerId: string;
  onBack: () => void;
}

const CustomerDetail: React.FC<CustomerDetailProps> = ({ customerId, onBack }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [logs, setLogs] = useState<AIUsageLog[]>([]);
  const [workspaces, setWorkspaces] = useState<CustomerWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'workspaces' | 'subscriptions' | 'invoices' | 'usage'>('overview');
  const { currencyCode } = useCurrency();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [c, subs, invs, usage] = await Promise.all([
          api.customers.getOne(customerId),
          api.subscriptions.getAll(),
          api.invoices.getAll(),
          api.usage.getLogs()
        ]);

        let wsList: CustomerWorkspace[] = [];
        try {
          const wsRes = await fetch(`/api/v1/customers/${customerId}/workspaces`);
          if (wsRes.ok) {
            wsList = await wsRes.json();
          }
        } catch {
          // fallback
        }
        
        setCustomer(c || null);
        setSubscriptions(subs.filter(s => s.customerId === customerId));
        setInvoices(invs.filter(i => i.customerId === customerId));
        setLogs(usage.filter(u => u.customerId === customerId || u.customerName === c?.name));
        setWorkspaces(wsList);
      } catch (e) {
        console.error("Error loading customer details", e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [customerId]);

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <svg className="animate-spin h-10 w-10 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  if (!customer) return null;

  const usageChartData = logs.reduce((acc: any[], log) => {
    const date = new Date(log.timestamp).toLocaleDateString(undefined, { weekday: 'short' });
    const existing = acc.find(i => i.day === date);
    if (existing) existing.usage += log.creditsConsumed;
    else acc.push({ day: date, usage: log.creditsConsumed });
    return acc;
  }, []).slice(0, 7);

  return (
    <div className="space-y-8 animate-in slide-in-from-right-8 duration-500 pb-24">
      <button onClick={onBack} className="flex items-center gap-2 text-slate-500 font-black uppercase tracking-[0.2em] text-[10px] hover:text-indigo-600 transition-colors">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        Back to CRM
      </button>

      <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] p-8 border border-slate-200 dark:border-slate-700 shadow-sm relative overflow-hidden group">
        <div className="relative z-10 flex flex-col md:flex-row justify-between gap-8">
           <div className="flex gap-6 items-center">
              <div className="w-20 h-20 bg-indigo-600 rounded-[2rem] flex items-center justify-center text-white text-3xl font-black shadow-xl shadow-indigo-100 dark:shadow-none">{customer.name.charAt(0)}</div>
              <div>
                 <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">{customer.name}</h1>
                 <p className="text-sm font-bold text-slate-400 mt-1">{customer.email} • {customer.id}</p>
                 <div className="flex gap-2 mt-4">
                    <span className="px-3 py-1 bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-700 rounded-lg text-[9px] font-black uppercase tracking-widest text-slate-500">{customer.location}</span>
                    <span className="px-3 py-1 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg text-[9px] font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-800">LTV: {formatValue(customer.totalLtv, currencyCode)}</span>
                 </div>
              </div>
           </div>
           
           <div className="flex gap-4">
              <div className="bg-indigo-900 dark:bg-black rounded-[2rem] p-6 text-white min-w-[200px] shadow-2xl">
                 <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-3">Predictive AI Burn</p>
                 {customer.credits.velocity ? (
                   <div className="space-y-1">
                      <p className="text-xs font-bold text-indigo-200">Zero Balance Date:</p>
                      <p className="text-2xl font-black text-emerald-400">{new Date(customer.credits.velocity.estimatedZeroDate).toLocaleDateString([], {month: 'short', day: 'numeric'})}</p>
                      <div className="flex items-center gap-2 mt-2">
                         <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                            <div className="h-full bg-indigo-500 w-[65%]"></div>
                         </div>
                         <span className="text-[9px] font-black text-indigo-300">IN 42 DAYS</span>
                      </div>
                   </div>
                 ) : (
                   <p className="text-sm font-bold text-indigo-200 italic">Insufficient data for velocity forecast.</p>
                 )}
              </div>
           </div>
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[100px] -mr-32 -mt-32"></div>
      </div>

      <div className="flex gap-1 bg-slate-100 dark:bg-slate-900 p-1 rounded-2xl w-fit">
        {(['overview', 'workspaces', 'subscriptions', 'invoices', 'usage'] as const).map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-white dark:bg-slate-800 text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-slate-600'}`}>
            {tab === 'workspaces' ? `Workspaces (${workspaces.length})` : tab}
          </button>
        ))}
      </div>

      <div className="min-h-[400px]">
        {activeTab === 'workspaces' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-800 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-700">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-3xl">
                  <Layers className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900 dark:text-white uppercase tracking-wider">
                    Linked SaaS Workspaces & Add-ons
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    SubScale synchronizes tenant IDs, names, environment metadata, and active workspace add-on licenses in real time.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="px-3.5 py-1.5 bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 rounded-xl text-xs font-black">
                  {workspaces.length} Registered {workspaces.length === 1 ? 'Workspace' : 'Workspaces'}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {workspaces.map((ws) => (
                <div
                  key={ws.id}
                  className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm space-y-4 hover:border-indigo-200 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-900 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-800">
                      {ws.isPrimary ? 'Primary Workspace' : 'Expansion Workspace'}
                    </span>
                    <span className="text-[10px] font-mono text-slate-400 font-bold">
                      {ws.environment || 'production'}
                    </span>
                  </div>

                  <div>
                    <h4 className="text-lg font-black text-slate-900 dark:text-white tracking-tight">{ws.name}</h4>
                    <p className="text-xs font-mono text-indigo-600 dark:text-indigo-400 mt-0.5">ID: {ws.id}</p>
                    <p className="text-[11px] text-slate-400 font-mono">Slug: {ws.slug || ws.id}</p>
                  </div>

                  <div className="pt-3 border-t border-slate-100 dark:border-slate-700 space-y-2">
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-400 block">
                      Active Workspace Add-ons ({ws.activeAddons?.length || 0})
                    </span>
                    {ws.activeAddons && ws.activeAddons.length > 0 ? (
                      <div className="space-y-1.5">
                        {ws.activeAddons.map((addon) => (
                          <div
                            key={addon.addonId}
                            className="flex items-center justify-between p-2 rounded-xl bg-slate-50 dark:bg-slate-900/60 border border-slate-100 dark:border-slate-800 text-xs"
                          >
                            <span className="font-bold text-slate-800 dark:text-slate-200 text-[11px]">{addon.name}</span>
                            <span className="font-black text-indigo-600 dark:text-indigo-400 text-[11px]">${addon.price}/mo</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">No workspace-scoped add-ons attached.</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {activeTab === 'subscriptions' && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
            <SubscriptionTable subscriptions={subscriptions} />
          </div>
        )}
        {activeTab === 'invoices' && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
            <InvoiceTable invoices={invoices} />
          </div>
        )}
        {activeTab === 'usage' && (
          <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
             <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 bg-white dark:bg-slate-800 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-700">
                   <div className="flex justify-between items-center mb-8">
                      <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Consumption Velocity</h3>
                      <div className="flex gap-2">
                         <span className="px-2 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 text-[8px] font-black rounded uppercase">Stabilizing</span>
                      </div>
                   </div>
                   <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={usageChartData}>
                          <defs>
                            <linearGradient id="colUsage" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#6366f1" stopOpacity={0.2}/>
                              <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" opacity={0.05} />
                          <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#94a3b8'}} />
                          <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 25px -5px rgb(0 0 0 / 0.1)' }} />
                          <Area type="monotone" dataKey="usage" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colUsage)" />
                        </AreaChart>
                      </ResponsiveContainer>
                   </div>
                </div>
                <div className="bg-slate-900 rounded-[2.5rem] p-8 text-white relative overflow-hidden flex flex-col justify-between">
                   <div>
                      <h3 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-6">Wallet Intelligence</h3>
                      <div className="space-y-6">
                         <div>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Current Balance</p>
                            <p className="text-3xl font-black text-white">{customer.credits.balance.toLocaleString()}</p>
                         </div>
                         <div>
                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Usage Velocity</p>
                            <p className="text-lg font-bold text-white">~{customer.credits.velocity?.currentDailyAverage || 0} credits / day</p>
                         </div>
                      </div>
                   </div>
                   <button className="w-full py-4 bg-indigo-600 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all mt-8">Configure Auto-Recharge</button>
                   <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 rounded-full blur-2xl -mr-16 -mt-16"></div>
                </div>
             </div>
             <InvoiceTable invoices={invoices.slice(0, 5)} />
          </div>
        )}
        {activeTab === 'overview' && (
           <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-in fade-in">
              <div className="bg-white dark:bg-slate-800 p-8 rounded-[2.5rem] border border-slate-200 dark:border-slate-700">
                 <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest mb-6">Recent Lifecycle Events</h3>
                 <div className="space-y-6">
                    <div className="flex gap-4">
                       <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600">✓</div>
                       <div>
                          <p className="text-xs font-black text-slate-900 dark:text-white uppercase">Invoice Paid</p>
                          <p className="text-xs text-slate-500 mt-0.5">Successful renewal of Enterprise Plan.</p>
                          <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase">2 days ago</p>
                       </div>
                    </div>
                    <div className="flex gap-4 opacity-50">
                       <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center text-slate-500">⚡</div>
                       <div>
                          <p className="text-xs font-black text-slate-900 dark:text-white uppercase">Dunning Alert Cleared</p>
                          <p className="text-xs text-slate-500 mt-0.5">Card updated and previous failure resolved.</p>
                          <p className="text-[10px] font-bold text-slate-400 mt-1 uppercase">12 days ago</p>
                       </div>
                    </div>
                 </div>
              </div>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 p-8 rounded-[2.5rem] border border-indigo-100 dark:border-indigo-800 flex flex-col justify-between">
                 <div>
                    <h3 className="text-sm font-black text-indigo-900 dark:text-indigo-300 uppercase tracking-widest mb-2">Account Strategy</h3>
                    <p className="text-xs text-indigo-700 dark:text-indigo-400 leading-relaxed font-medium">Customer is exhibiting high expansion intent. AI recommends reaching out with a "Volume Commitment" proposal for Q3 to lock in 20% growth.</p>
                 </div>
                 <button className="px-6 py-3 bg-white dark:bg-slate-800 rounded-xl text-[10px] font-black text-indigo-600 dark:text-indigo-400 uppercase tracking-widest border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-600 hover:text-white transition-all w-fit">Draft Growth Proposal</button>
              </div>
           </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetail;
